import { CheckoutProvider } from '@stripe/react-stripe-js/checkout';
import React, { FunctionComponent, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { Color } from '../../config/Color';
import { useStripeClient } from '../../helpers/Stripe';
import { ActionType } from '../../store/ActionTypes';
import { useAppDispatch, useAppStore } from '../../store/Store';
import { Analytics } from '../../util/Analytics';
import { appOverride } from '../../util/AppUtils';
import { trackPaymentAnalyticsEvent } from '../../util/PaymentAnalytics';
import { ContentLoading } from '../styles/ContentLoading';
import { ErrorMessage } from '../styles/ErrorMessage';
import { stripeElementsOptionsFonts } from '../styles/Typography';
import { CollectCardForm, CollectCardFormProps } from './CollectCardForm';

export type CheckoutProps = Pick<CollectCardFormProps, 'nextPage'>;

export const Checkout: FunctionComponent<CheckoutProps> = (props) => {
  const dispatch = useAppDispatch();
  const purchase = useAppStore((state) => state.purchase);
  // Checkout intents saved before dual-account support do not include stripeAccount. Those sessions
  // were created on AU, so this fallback resumes them with the matching publishable key. New checkout
  // responses always provide stripeAccount.
  const stripeAccount = purchase.intent.stripeAccount ?? 'au';
  const stripePromise = useStripeClient(stripeAccount);
  const theme = useTheme();

  useEffect(
    function createCheckout() {
      if (purchase.intent.clientSecret === undefined) {
        dispatch({ type: ActionType.CREATE_CHECKOUT });
      }
    },
    [purchase.intent.clientSecret]
  );

  useEffect(() => {
    trackPaymentAnalyticsEvent(Analytics.Event.o_payment_started);
  }, []);

  const isNerva = appOverride({ nerva: true, default: false });
  const appearance = createAppearance(isNerva, theme.font.primary);

  if (purchase.intent.error !== undefined) {
    return <ErrorMessage data-test="intentError">{purchase.intent.error.message}</ErrorMessage>;
  }

  return (
    <>
      <span style={{ display: 'none' }} data-test="intentPlan">
        {purchase.plan}
      </span>
      <span style={{ display: 'none' }} data-test="trialPeriod">
        {purchase.trialPeriodDays}
      </span>
      {purchase.intent.loading || purchase.intent.clientSecret === undefined ? (
        <ContentLoading />
      ) : (
        <CheckoutProvider
          key={`${stripeAccount}:${purchase.intent.checkoutId}`}
          stripe={stripePromise}
          options={{
            clientSecret: purchase.intent.clientSecret,
            elementsOptions: {
              fonts: stripeElementsOptionsFonts,
              appearance,
              savedPaymentMethod: {
                enableSave: 'never',
                enableRedisplay: 'never',
              },
            },
          }}
        >
          <CollectCardForm nextPage={props.nextPage} />
        </CheckoutProvider>
      )}
    </>
  );
};

// Shared container styling for .Block, .TabPanel, .AccordionItem
const containerStyle = {
  border: `1px solid ${Color.decorative.default}`,
  borderRadius: '8px',
  boxShadow: 'none',
};

function createAppearance(isNerva: boolean, fontFamily: string) {
  return {
    theme: (isNerva ? 'night' : 'flat') as 'night' | 'flat',
    variables: {
      fontFamily,
      fontSizeBase: '14px',
      spacingUnit: isNerva ? '4px' : '12px',
      colorBackground: isNerva ? '#0A1412' : 'rgb(247, 248, 250)',
      colorPrimary: isNerva ? '#FFFFFF' : Color.content.accent,
      colorText: isNerva ? '#FFFFFF' : Color.content.default,
      colorDanger: Color.state.error,
      borderRadius: isNerva ? '14px' : '8px',
    },
    rules: {
      '.Block': containerStyle,
      '.TabPanel': containerStyle,
      '.AccordionItem': containerStyle,
      '.Input': {
        border: `1px solid ${Color.decorative.default}`,
        borderRadius: isNerva ? '14px' : '8px',
        padding: isNerva ? '16px' : '12px',
        boxShadow: 'none',
      },
      '.Input:focus': {
        border: isNerva
          ? `2px solid ${Color.content.default}`
          : `2px solid ${Color.content.accent}`,
        boxShadow: 'none',
      },
      '.Label': {
        fontSize: '14px',
        fontWeight: '400',
      },
    },
  };
}
